// @ts-nocheck
/**
 * Approve or deny a media credential request. Denying asks for a reason, which
 * is stored on the request; who reviewed it and when are recorded server-side.
 */
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function MediaReviewDialog({
  open,
  onOpenChange,
  request,
  onDecide,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: any;
  onDecide: (
    decision: "approved" | "denied",
    reason: string
  ) => Promise<{ ok: boolean; error?: string }>;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason(request?.denial_reason ?? "");
      setError(null);
    }
  }, [open, request]);

  const decide = async (decision: "approved" | "denied") => {
    if (decision === "denied" && !reason.trim()) {
      setError("Tell them why — the reason is stored on the request.");
      return;
    }
    setBusy(true);
    setError(null);
    const res = await onDecide(decision, reason.trim());
    setBusy(false);
    if (res.ok) onOpenChange(false);
    else setError(res.error || "Couldn't record that decision.");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{request?.name || "Media request"}</DialogTitle>
          <DialogDescription>
            {[request?.organization, request?.coverage_type, request?.email]
              .filter(Boolean)
              .join(" · ") || "Review this credential request."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="deny-reason">Reason (required to deny)</Label>
          <Textarea
            id="deny-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Credentials are full for this event."
          />
        </div>

        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => decide("denied")} disabled={busy}>
            Deny
          </Button>
          <Button onClick={() => decide("approved")} disabled={busy}>
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
